// Shared business-hours + after-hours-presence-report state for the per-location Location settings
// page. Lifted out of the Presence Control Center controller when these two settings moved
// to /manage/location-settings: business hours lives in the "Workplace" section and the
// after-hours presence report lives in a new "Security" section. Because the two sections render in
// different places on the page but share one underlying presence-settings document, a single instance
// of this manager is created in the route/controller and passed to both components — so toggling
// business hours in one section immediately gates the report in the other, and either Save preserves
// the rest of the location's presence-settings document.
//
// It is a plain tracked class (services passed in, not injected) so it can be unit-tested with mocks.
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type LocationModel from 'garaje/models/location';
import type LocationRoleModel from 'garaje/models/location-role';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';

import {
  buildBusinessHoursDays,
  canonicalizeBusinessHours,
  DEFAULT_BUSINESS_HOURS_END,
  DEFAULT_BUSINESS_HOURS_START,
  type BusinessHoursDayDraft,
  type BusinessHoursPayload,
  type DayWindowPayload,
} from './business-hours';
import type { MultiSignalPolicyPayload, PresenceSettingsResponse } from './presence-settings-types';

// A candidate/selected recipient for the after-hours presence report. Users only (roles are out of
// scope — matches the PCC behavior and apps/settings).
export interface AfterHoursRecipientUser {
  id: string;
  name: string;
}

// Shape of the notification-recipients GET response.
interface AfterHoursRecipientData {
  attributes: {
    recipientType: string;
    recipientValue: string;
  };
}

interface AfterHoursRecipientsApiResponse {
  data?: AfterHoursRecipientData[];
}

// Services the manager needs to persist. Passed in (not injected) so tests can hand it mocks.
export interface BusinessHoursManagerServices {
  ajax: AjaxService;
  flashMessages: FlashMessagesService;
  state: StateService;
}

// The data the route loads up front (one presence-settings GET + the recipients lookup). Seeds the
// manager so the page renders without each component fetching on its own.
export interface PresenceBusinessHoursData {
  response: PresenceSettingsResponse | null;
  recipientOptions: AfterHoursRecipientUser[];
  selectedRecipients: AfterHoursRecipientUser[];
}

// Load everything the two sections need for `locationId`, up front, from the route's model hook:
//   (1) the full presence-settings document (so a Save can preserve the sign-in/sign-out/policy
//       blocks it isn't editing), and
//   (2) the after-hours report's candidate users (location-level + company-level role-holders,
//       deduped) plus the currently-saved recipients (alertType=after_hours_presence).
// Resilient: any failure degrades to safe defaults (null response / empty recipients) rather than
// failing the whole page model.
export async function loadPresenceBusinessHoursData(
  ajax: AjaxService,
  store: StoreService,
  locationId: string,
  companyId: string,
): Promise<PresenceBusinessHoursData> {
  const data: PresenceBusinessHoursData = { response: null, recipientOptions: [], selectedRecipients: [] };
  if (!locationId) return data;

  try {
    data.response = await ajax.request(urlBuilder.presenceSettingsUrl(locationId), {
      method: 'GET',
    });
  } catch (_error) {
    // Leave response null — the sections render with closed/default business hours.
    data.response = null;
  }

  try {
    // (a) Candidate users — location-level + company-level role assignments, deduped by user id.
    const [locationRoles, companyRoles] = await Promise.all([
      store.query('location-role', { filter: { location: locationId }, include: 'user' }),
      companyId
        ? store.query('location-role', { filter: { company: companyId }, include: 'user' })
        : Promise.resolve([] as unknown as LocationRoleModel[]),
    ]);

    const usersMap = new Map<string, AfterHoursRecipientUser>();
    const processRoleAssignments = async (roleAssignments: LocationRoleModel[]): Promise<void> => {
      for (const roleAssignment of roleAssignments) {
        try {
          const user = await roleAssignment.user;
          if (user?.id && !usersMap.has(user.id)) {
            // eslint-disable-next-line ember/use-ember-get-and-set
            usersMap.set(user.id, { id: user.id, name: user.fullName || user.email || 'Unknown User' });
          }
        } catch (_error) {
          // Skip a user we can't resolve rather than failing the whole list.
        }
      }
    };
    await processRoleAssignments(locationRoles.slice());
    await processRoleAssignments(companyRoles.slice());
    data.recipientOptions = Array.from(usersMap.values());

    // (b) Current recipients — notification-recipients GET, users only, mapped to the candidate list.
    const response: AfterHoursRecipientsApiResponse = await ajax.request(
      urlBuilder.locationNotificationRecipientsUrl(locationId, 'after_hours_presence'),
      {
        // @ts-ignore accept is not a valid attribute for request options
        accept: 'application/vnd.api+json',
      },
    );
    if (response?.data && response.data.length > 0) {
      data.selectedRecipients = response.data
        .filter((r) => r.attributes.recipientType === 'user')
        .map((r) => {
          const userId = r.attributes.recipientValue;
          return data.recipientOptions.find((u) => u.id === userId) || { id: userId, name: `User ${userId}` };
        });
    }
  } catch (_error) {
    data.recipientOptions = [];
    data.selectedRecipients = [];
  }

  return data;
}

export default class BusinessHoursManager {
  #ajax: AjaxService;
  #flashMessages: FlashMessagesService;
  #state: StateService;

  location: LocationModel;

  // ---- Business hours draft (Workplace section) -------------------------------------------
  @tracked businessHoursEnabled: boolean;
  @tracked businessHoursDays: BusinessHoursDayDraft[];
  @tracked persistedBusinessHours: BusinessHoursPayload | null;

  // ---- After-hours presence report draft (Security section) --------------------------------------
  @tracked afterHoursReportEnabled: boolean;
  @tracked afterHoursRecipientOptions: AfterHoursRecipientUser[];
  @tracked afterHoursSelectedRecipients: AfterHoursRecipientUser[];
  @tracked persistedAfterHoursRecipientIds: string[];

  @tracked isSaving = false;
  @tracked error: string | null = null;

  // Whether the Business hours panel is expanded. Lifted onto the manager (rather than kept local to
  // the panel) so the Security section's "Set now" link can open it the same way its Enable button does.
  @tracked businessHoursPanelOpen = false;

  // The presence-settings blocks this page doesn't edit, held verbatim from the load so a Save can
  // send them back unchanged (the PUT replaces the whole document).
  #settings: PresenceSettingsResponse['data']['attributes']['settings'] | null;
  #signoutSettings: PresenceSettingsResponse['data']['attributes']['signout-settings'] | null;
  #multiSignalPolicy: MultiSignalPolicyPayload | null;

  constructor(services: BusinessHoursManagerServices, location: LocationModel, data: PresenceBusinessHoursData) {
    this.#ajax = services.ajax;
    this.#flashMessages = services.flashMessages;
    this.#state = services.state;
    this.location = location;

    const businessHours = data.response?.data?.attributes?.['business-hours'];
    this.businessHoursEnabled = businessHours?.enabled ?? false;
    this.afterHoursReportEnabled = businessHours?.['after-hours-report-enabled'] ?? false;
    this.businessHoursDays = buildBusinessHoursDays(businessHours);
    this.persistedBusinessHours = businessHours
      ? {
          enabled: businessHours.enabled,
          'after-hours-report-enabled': businessHours['after-hours-report-enabled'] ?? false,
          days: businessHours.days ?? {},
        }
      : null;

    this.afterHoursRecipientOptions = data.recipientOptions;
    this.afterHoursSelectedRecipients = data.selectedRecipients;
    this.persistedAfterHoursRecipientIds = data.selectedRecipients.map((u) => u.id);

    this.#settings = data.response?.data?.attributes?.settings ?? null;
    this.#signoutSettings = data.response?.data?.attributes?.['signout-settings'] ?? null;
    const rawPolicy = data.response?.data?.attributes?.['multi-signal-policy'] ?? null;
    if (rawPolicy) {
      // Normalize legacy `required-signal-types` (old flat array) to the new policies shape so
      // every write path sends `signal-type-combinations` regardless of when the response was cached.
      const policies =
        rawPolicy['signal-type-combinations'] ??
        (rawPolicy['required-signal-types'] ? [rawPolicy['required-signal-types']] : []);
      this.#multiSignalPolicy = {
        enabled: rawPolicy.enabled,
        'signal-type-combinations': policies,
        'corroboration-window-minutes': rawPolicy['corroboration-window-minutes'],
      };
    } else {
      this.#multiSignalPolicy = null;
    }
  }

  // ---- Getters -----------------------------------------------------------------------------------

  // Business hours is "configured" once it's been saved with the master switch on — the state the
  // after-hours report (and PCC's after-hours sign-in/out) depends on. Gated on the *persisted*
  // config, not the unsaved draft.
  get businessHoursConfigured(): boolean {
    return this.persistedBusinessHours !== null && this.persistedBusinessHours.enabled;
  }

  // The after-hours report has a saved configuration once its opt-in is persisted on — drives the
  // report panel's Edit-vs-Enable header button.
  get reportConfigured(): boolean {
    return this.#persistedReportOptIn();
  }

  // Saveable state: master off, or every enabled day has a complete, ordered window.
  get businessHoursValid(): boolean {
    return !this.businessHoursEnabled || this.businessHoursDays.every((day) => day.valid);
  }

  // Candidate users minus the already-selected ones, sorted by name (so the dropdown never re-offers a
  // pick).
  get afterHoursAvailableRecipients(): AfterHoursRecipientUser[] {
    const selectedIds = new Set(this.afterHoursSelectedRecipients.map((u) => u.id));
    return this.afterHoursRecipientOptions
      .filter((user) => !selectedIds.has(user.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // The per-day windows in API shape, built from the editable day drafts.
  #daysPayload(): Record<string, DayWindowPayload> {
    const days: Record<string, DayWindowPayload> = {};
    this.businessHoursDays.forEach((day) => {
      days[day.key] = day.apiWindow;
    });
    return days;
  }

  // The report opt-in as last saved (false when nothing is persisted).
  #persistedReportOptIn(): boolean {
    return this.persistedBusinessHours?.['after-hours-report-enabled'] ?? false;
  }

  // The Workplace concern: the master toggle + per-day windows differ from what's persisted.
  // The report opt-in is normalized out of the comparison so a report-only change doesn't light up
  // the Business hours Save. An invalid day counts as dirty so the user isn't stuck — the Save still
  // blocks on `businessHoursValid`.
  get configDirty(): boolean {
    if (!this.businessHoursValid) return true;
    const persisted = this.persistedBusinessHours;
    if (!this.businessHoursEnabled && persisted === null) return false;
    if (persisted === null) return true;
    const normalize = (enabled: boolean, days: Record<string, DayWindowPayload>): string =>
      JSON.stringify(canonicalizeBusinessHours({ enabled, 'after-hours-report-enabled': false, days }));
    return normalize(this.businessHoursEnabled, this.#daysPayload()) !== normalize(persisted.enabled, persisted.days);
  }

  // The Security concern: the report opt-in differs from what's persisted. Only meaningful while
  // business hours stays enabled in the draft — disabling business hours is a Workplace action
  // (its Save clears the opt-in), so it must not independently light up the report Save.
  get reportOptInDirty(): boolean {
    if (!this.businessHoursEnabled) return false;
    return this.afterHoursReportEnabled !== this.#persistedReportOptIn();
  }

  // The recipient selection differs from the last saved snapshot. Only meaningful when the report is
  // on (when it's off the opt-in gates sending).
  get afterHoursRecipientsDirty(): boolean {
    if (!this.afterHoursReportEnabled) return false;
    const selectedIds = this.afterHoursSelectedRecipients.map((u) => u.id).sort();
    const persistedIds = [...this.persistedAfterHoursRecipientIds].sort();
    return JSON.stringify(selectedIds) !== JSON.stringify(persistedIds);
  }

  // Any unsaved change across either section — wired into the page's unsaved-changes guard.
  get isDirty(): boolean {
    return this.configDirty || this.reportOptInDirty || this.afterHoursRecipientsDirty;
  }

  // Business hours Save (Workplace): persists the master toggle + per-day windows only.
  get canSaveBusinessHours(): boolean {
    return this.businessHoursValid && this.configDirty;
  }

  get businessHoursSaveDisabled(): boolean {
    return !this.canSaveBusinessHours || this.isSaving;
  }

  // Report Save (Security): persists the report opt-in and/or its recipients only.
  get canSaveAfterHoursReport(): boolean {
    return this.businessHoursValid && (this.reportOptInDirty || this.afterHoursRecipientsDirty);
  }

  get afterHoursReportSaveDisabled(): boolean {
    return !this.canSaveAfterHoursReport || this.isSaving;
  }

  // ---- Mutations (draft only; nothing persists until a Save) -------------------------------------

  toggleBusinessHours = (enabled: boolean): void => {
    this.businessHoursEnabled = enabled;
    // Turning business hours off also drops the report opt-in (kept consistent with the forced-false
    // on save).
    if (!enabled) this.afterHoursReportEnabled = false;
  };

  // Begin configuring business hours: turn the draft on and expand the panel. This is the "Enable"
  // behavior, shared by the Business hours panel's Enable button and the report notice's "Set now" link.
  startConfiguringBusinessHours = (): void => {
    this.toggleBusinessHours(true);
    this.businessHoursPanelOpen = true;
  };

  toggleBusinessHoursDay = (day: BusinessHoursDayDraft, enabled: boolean): void => {
    day.enabled = enabled;
    // Newly opened days default to a 9–5 window, but keep any times the day already carries (e.g. it
    // was configured, toggled off without clearing, and is being re-enabled).
    if (enabled && (!day.start || !day.end)) {
      day.start = DEFAULT_BUSINESS_HOURS_START;
      day.end = DEFAULT_BUSINESS_HOURS_END;
    }
  };

  setBusinessHoursDayStart = (day: BusinessHoursDayDraft, time: string): void => {
    day.start = time;
  };

  setBusinessHoursDayEnd = (day: BusinessHoursDayDraft, time: string): void => {
    day.end = time;
  };

  toggleAfterHoursReport = (enabled: boolean): void => {
    this.afterHoursReportEnabled = enabled;
  };

  updateAfterHoursRecipients = (selected: AfterHoursRecipientUser[]): void => {
    this.afterHoursSelectedRecipients = Array.from(new Map(selected.map((user) => [user.id, user])).values());
  };

  // Reset the Workplace draft (master toggle + per-day windows) to the persisted snapshot.
  rollbackBusinessHours = (): void => {
    const persisted = this.persistedBusinessHours;
    this.businessHoursEnabled = persisted?.enabled ?? false;
    this.businessHoursDays = buildBusinessHoursDays(persisted ? { days: persisted.days } : null);
  };

  // Reset the Security draft (report opt-in + recipients) to the persisted snapshot.
  rollbackAfterHoursReport = (): void => {
    this.afterHoursReportEnabled = this.#persistedReportOptIn();
    this.afterHoursSelectedRecipients = this.persistedAfterHoursRecipientIds.map(
      (id) => this.afterHoursRecipientOptions.find((u) => u.id === id) ?? { id, name: `User ${id}` },
    );
  };

  // Reset every draft back to the persisted snapshot — used by the page's "discard unsaved changes"
  // path so leaving the page abandons in-progress edits cleanly.
  rollback = (): void => {
    this.rollbackBusinessHours();
    this.rollbackAfterHoursReport();
  };

  // ---- Persistence -------------------------------------------------------------------------------

  // Save the business-hours block (Workplace Save). Validates, PUTs the full presence-settings
  // document preserving the other blocks, and promotes the persisted snapshot on success.
  // Returns true when the save succeeded (or there was nothing to save), false when it was blocked
  // by validation or failed — so the caller knows whether to collapse the panel.
  saveBusinessHours = async (): Promise<boolean> => {
    if (!this.businessHoursValid) {
      const offending = this.businessHoursDays.find((day) => day.enabled && !day.rangeValid);
      const message =
        offending?.outOfOrder === true
          ? `${offending.label}: start time must be before the end time`
          : 'Set a start and end time for each open day';
      this.#flashMessages.showAndHideFlash('error', message);
      return false;
    }
    if (!this.configDirty) return true;

    this.isSaving = true;
    this.error = null;
    try {
      const payload = this.#businessHoursConfigPayload();
      await this.#putPresenceSettings(payload);
      this.persistedBusinessHours = payload;
      // Keep the report opt-in in sync — disabling business hours clears it.
      this.afterHoursReportEnabled = payload['after-hours-report-enabled'];
      this.#flashMessages.showAndHideFlash('success', 'Business hours saved');
      return true;
    } catch (_error) {
      this.error = 'Failed to save business hours';
      this.#flashMessages.showAndHideFlash('error', 'Failed to save business hours');
      return false;
    } finally {
      this.isSaving = false;
    }
  };

  // Save the after-hours report (Security Save): the opt-in (in the business-hours block) and/or its
  // custom recipients. Each is persisted only when it changed; the opt-in PUT overlays the
  // opt-in onto the *persisted* config so it never commits unsaved Workplace edits.
  // Returns true when the save succeeded, false when it failed — so the caller knows whether to
  // collapse the panel.
  saveAfterHoursReport = async (): Promise<boolean> => {
    if (!this.businessHoursValid) return false;

    this.isSaving = true;
    this.error = null;
    try {
      if (this.reportOptInDirty) {
        const payload = this.#reportOptInPayload();
        await this.#putPresenceSettings(payload);
        this.persistedBusinessHours = payload;
      }

      if (this.afterHoursRecipientsDirty) {
        await this.#putRecipients();
        this.persistedAfterHoursRecipientIds = this.afterHoursSelectedRecipients.map((u) => u.id);
      }

      this.#flashMessages.showAndHideFlash('success', 'Outside business hours presence report saved');
      return true;
    } catch (_error) {
      this.error = 'Failed to save outside business hours presence report';
      this.#flashMessages.showAndHideFlash('error', 'Failed to save outside business hours presence report');
      return false;
    } finally {
      this.isSaving = false;
    }
  };

  // Workplace payload: the master toggle + per-day windows from the draft, preserving the
  // persisted report opt-in (forced off when disabling — the report can't fire without a window).
  #businessHoursConfigPayload(): BusinessHoursPayload {
    return {
      enabled: this.businessHoursEnabled,
      'after-hours-report-enabled': this.businessHoursEnabled && this.#persistedReportOptIn(),
      days: this.#daysPayload(),
    };
  }

  // Security payload: the report opt-in from the draft, leaving the persisted master toggle + windows
  // untouched (the report Save is only reachable once business hours is configured).
  #reportOptInPayload(): BusinessHoursPayload {
    const persisted = this.persistedBusinessHours;
    return {
      enabled: persisted?.enabled ?? this.businessHoursEnabled,
      'after-hours-report-enabled': this.afterHoursReportEnabled,
      days: persisted?.days ?? this.#daysPayload(),
    };
  }

  // PUT the full presence-settings document: the given business-hours block plus the other blocks sent
  // back verbatim from the load (the endpoint replaces the whole document). The location's IANA
  // timezone is stamped onto business hours at save time.
  async #putPresenceSettings(businessHours: BusinessHoursPayload): Promise<void> {
    const businessHoursPayload = { ...businessHours, timezone: this.location.timezone };

    const attributes: Record<string, unknown> = {
      settings: this.#settings,
      'signout-settings': this.#signoutSettings,
      'business-hours': businessHoursPayload,
    };
    // Only echo back a policy that already existed — omitting it leaves any stored value untouched.
    if (this.#multiSignalPolicy) {
      attributes['multi-signal-policy'] = this.#multiSignalPolicy;
    }

    await this.#ajax.request(urlBuilder.presenceSettingsUrl(this.location.id), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: { data: { type: 'presence-settings', attributes } },
    });
  }

  // PUT the after-hours report's custom recipients (users only). Throws on failure.
  async #putRecipients(): Promise<void> {
    const lastModifiedByUserId = this.#state.currentUser?.id;
    const validUserIds = new Set(this.afterHoursRecipientOptions.map((u) => u.id));
    const recipients = this.afterHoursSelectedRecipients
      .filter((user) => validUserIds.has(user.id))
      .map((user) => ({ recipientType: 'user', recipientValue: String(user.id) }));

    await this.#ajax.request(urlBuilder.locationNotificationRecipientsUrl(this.location.id, 'after_hours_presence'), {
      type: 'PUT',
      contentType: 'application/json',
      // @ts-ignore accept is not a valid attribute for request options
      accept: 'application/vnd.api+json',
      data: { alertType: 'after_hours_presence', recipients, lastModifiedByUserId },
    });
  }
}
